import React, { useState } from 'react';
import { confirmDelete } from '../../../utils/ecxSwal';
import mediaService from '../../../services/media.service';
import MediaImagePreviewModal from './subparts/MediaImagePreviewModal';

function formatFileSize(bytes) {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`;
}

function MediaListItem({ item, updateMediaList }) {
    const [isCopied, setIsCopied] = useState(false);
    const [showPreview, setShowPreview] = useState(false);

    const handleDelete = async () => {
        const result = await confirmDelete({
            title: 'Delete Media?',
            itemName: item.name,
            description: 'This file will be permanently removed from your clinic media library.',
            onConfirm: () => mediaService.deleteMedia(item.clinicId, [item.id]),
        });

        if (result.isConfirmed) {
            updateMediaList();
        }
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(item.path);
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 2000);
        } catch {
            const textArea = document.createElement('textarea');
            textArea.value = item.path;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 2000);
        }
    };

    const fileType = item.type?.split('/').pop()?.toUpperCase() || 'FILE';
    const isImage = item.type?.startsWith('image/');
    const isPdf = item.type === 'application/pdf' || item.name?.toLowerCase().endsWith('.pdf');
    const isPreviewableImage = isImage && !isPdf;

    const handlePreviewOpen = () => {
        if (isPreviewableImage) {
            setShowPreview(true);
        }
    };

    return (
        <>
        <div className="my-files-item ecx-page-list-item h-100 d-flex flex-column">
            <div
                className={`my-files-item__preview${isPreviewableImage ? ' is-clickable' : ''}`}
                onClick={handlePreviewOpen}
                onKeyDown={(event) => {
                    if (isPreviewableImage && (event.key === 'Enter' || event.key === ' ')) {
                        event.preventDefault();
                        handlePreviewOpen();
                    }
                }}
                role={isPreviewableImage ? 'button' : undefined}
                tabIndex={isPreviewableImage ? 0 : undefined}
                title={isPreviewableImage ? 'View image' : undefined}
            >
                {isImage ? (
                    <img src={item.path} alt={item.name} loading="lazy" />
                ) : (
                    <div className="my-files-item__preview-fallback">
                        <i className="bi bi-file-earmark-text"></i>
                    </div>
                )}
                <span className="my-files-item__type-badge">{fileType}</span>
            </div>

            <div className="my-files-item__body flex-grow-1 d-flex flex-column">
                <h6 className="my-files-item__title fw-semibold mb-2" title={item.name}>
                    {item.name}
                </h6>

                <div className="my-files-item__meta mb-2">
                    <span title={item.modifiedAt}>
                        <i className="bi bi-calendar3"></i>
                        {item.modifiedAt}
                    </span>
                    <span>
                        <i className="bi bi-hdd"></i>
                        {formatFileSize(item.sizeBytes)}
                    </span>
                </div>

                <div className="my-files-item__path mt-auto">
                    <div className="input-group input-group-sm">
                        <input
                            type="text"
                            className="form-control form-control-sm bg-light"
                            value={item.path}
                            readOnly
                            disabled
                            aria-label="File URL"
                        />
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={handleCopy}
                            disabled={isCopied}
                            title="Copy URL"
                        >
                            {isCopied ? (
                                <i className="bi bi-check2 text-success"></i>
                            ) : (
                                <i className="bi bi-copy"></i>
                            )}
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-danger"
                            onClick={handleDelete}
                            title="Delete file"
                        >
                            <i className="bi bi-trash"></i>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        {isPreviewableImage && (
            <MediaImagePreviewModal show={showPreview} onClose={() => setShowPreview(false)} item={item} />
        )}
        </>
    );
}

export default MediaListItem;
